import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { api } from "../api";
import { icons } from "../company-ui";

export interface Notice { id: string; kind: string; title: string; body: string; link: string | null; entity: string | null; entityId: string | null; createdAt: string; read: boolean }
export const NOTICES_KEY = ["me", "notifications"] as const;

/** "Notices" or "Notices, 3 unread" (v31 bell31). */
export const bellLabel = (n: number) => (n ? `Notices, ${n} unread` : "Notices");

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * The notices bell (ALN-26 D5, approved v31 screen): left of the name pill, the caller's own
 * undismissed notices. Opening one marks it read and follows its link; Dismiss hides it for this
 * person only, here, on the owning page and in Needs attention.
 */
export function NoticesBell() {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: NOTICES_KEY, queryFn: () => api<{ notices: Notice[]; unread: number }>("/me/notifications"), refetchInterval: 60_000, refetchOnWindowFocus: true });
  const refresh = () => { void qc.invalidateQueries({ queryKey: NOTICES_KEY }); void qc.invalidateQueries({ queryKey: ["job-failures"] }); };
  const read = useMutation({ mutationFn: (id: string) => api(`/me/notifications/${id}/read`, { body: {} }), onSettled: refresh });
  const dismiss = useMutation({ mutationFn: (id: string) => api(`/me/notifications/${id}/dismiss`, { body: {} }), onSettled: refresh });
  const notices = q.data?.notices ?? [];
  const unread = q.data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>("button")?.focus();
    const away = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); button.current?.focus(); } };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  const openNotice = (n: Notice) => {
    setOpen(false);
    if (!n.read) read.mutate(n.id);
    if (n.link) navigate(n.link);
    else button.current?.focus();
  };
  const dismissNotice = (n: Notice, i: number) => {
    dismiss.mutate(n.id, {
      onSuccess: () => {
        // keep focus in the list: the next notice's button, else the bell
        const next = panel.current?.querySelectorAll<HTMLElement>(".bell__open")[i + 1] ?? panel.current?.querySelectorAll<HTMLElement>(".bell__open")[i - 1];
        (next ?? panel.current?.querySelector<HTMLElement>("button") ?? button.current)?.focus();
      },
    });
  };

  return (
    <div className="bell" ref={wrap}>
      <button ref={button} type="button" className="iconbtn bell__btn" aria-label={bellLabel(unread)} aria-expanded={open} aria-controls="bell-panel" onClick={() => setOpen(!open)}>
        {icons.bell}
        {unread > 0 && <span className="bell__n" aria-hidden="true">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="bell__panel" id="bell-panel" role="region" aria-labelledby="bell-title" ref={panel}>
          <p className="bell__h" id="bell-title"><b>Notices</b></p>
          {notices.length ? (
            <ul className="bell__list">
              {notices.map((n, i) => (
                <li key={n.id} className={`bell__item${n.read ? "" : " is-unread"}`}>
                  <button type="button" className="bell__open" onClick={() => openNotice(n)}>
                    <b>{n.title}</b>{n.read ? null : <span className="sr"> (unread)</span>}
                    <span className="small">{n.body}</span>
                    <span className="small muted">{when(n.createdAt)}</span>
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost bell__x" disabled={dismiss.isPending} onClick={() => dismissNotice(n, i)}>
                    Dismiss<span className="sr"> {n.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="bell__empty muted">{q.isError ? "Notices didn't load. Try again in a minute." : q.isPending ? "Loading notices…" : "No notices."}</p>
          )}
        </div>
      )}
    </div>
  );
}
